import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router';
import { api } from '../../../api.js';
import { useAuth } from '../../../auth/AuthProvider.js';
import type {
  Account,
  AccountNode,
  TaxRate,
  Vendor,
  WithholdingRate,
} from '../../../types/bookkeeping.js';
import type { ConfigStatus } from '../../../types/onboarding.js';

const FX_SCALE = 1_000_000_000n;
const TAX_DIVISOR = 100_000_000n;

function flattenTree(nodes: AccountNode[]): Account[] {
  const result: Account[] = [];
  function walk(ns: AccountNode[]) {
    for (const n of ns) {
      result.push(n);
      if (n.children.length > 0) walk(n.children);
    }
  }
  walk(nodes);
  return result;
}

interface CheckLineForm {
  account_id: string;
  description: string;
  amount: string;
  tax_rate_id: string;
}

interface CheckWHForm {
  withholding_rate_id: string;
  base_amount: string;
}

function defaultLine(): CheckLineForm {
  return { account_id: '', description: '', amount: '0', tax_rate_id: '' };
}

interface CheckDetail {
  check: {
    id: string;
    number: string;
    check_number: string | null;
    payee_type: 'vendor' | 'employee' | 'other';
    payee_id: string | null;
    payee_name: string;
    payment_date: string;
    payment_account_id: string;
    currency: string;
    fx_rate: string;
    subtotal: string;
    tax_total: string;
    withholding_total: string;
    grand_total: string;
    net_amount: string;
    status: string;
    memo: string | null;
  };
  lines: Array<{
    id: string;
    line_number: number;
    account_id: string;
    description: string | null;
    amount: string;
    tax_rate_id: string | null;
    tax_amount: string;
  }>;
  withholdings: Array<{
    id: string;
    withholding_rate_id: string;
    base_amount: string;
    withheld_amount: string;
  }>;
}

export function CheckForm() {
  const { t } = useTranslation('bookkeeping');
  const nav = useNavigate();
  const qc = useQueryClient();
  const { activeOrgId } = useAuth();
  const { id: checkId } = useParams<{ id: string }>();
  const isNew = !checkId;

  const today = new Date().toISOString().slice(0, 10);
  const [payeeType, setPayeeType] = useState<'vendor' | 'employee' | 'other'>('vendor');
  const [payeeId, setPayeeId] = useState('');
  const [payeeName, setPayeeName] = useState('');
  const [checkNumber, setCheckNumber] = useState('');
  const [paymentDate, setPaymentDate] = useState(today);
  const [paymentAccountId, setPaymentAccountId] = useState('');
  const [currency, setCurrency] = useState('');
  const [memo, setMemo] = useState('');
  const [lines, setLines] = useState<CheckLineForm[]>([defaultLine()]);
  const [withholdings, setWithholdings] = useState<CheckWHForm[]>([]);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [seeded, setSeeded] = useState(false);

  const { data: configData } = useQuery({
    queryKey: ['config-status', activeOrgId],
    queryFn: () => api<ConfigStatus>(`/orgs/${activeOrgId}/config-status`),
    enabled: !!activeOrgId,
  });
  const baseCurrency = configData?.organization.baseCurrency ?? 'USD';
  const effectiveCurrency = currency || baseCurrency;
  const sameCurrency = effectiveCurrency === baseCurrency || effectiveCurrency.length !== 3;

  const { data: fxData } = useQuery({
    queryKey: ['fx', effectiveCurrency, baseCurrency, paymentDate],
    queryFn: () =>
      api<{ rate: string; source: string }>(
        `/bookkeeping/fx-rates?date=${paymentDate}&from=${effectiveCurrency}&to=${baseCurrency}`,
      ),
    enabled: !!effectiveCurrency && effectiveCurrency.length === 3 && !sameCurrency,
    retry: false,
  });

  const { data: vendorsData } = useQuery({
    queryKey: ['bookkeeping-vendors', activeOrgId],
    queryFn: () => api<{ vendors: Vendor[] }>('/bookkeeping/vendors?limit=200'),
    enabled: !!activeOrgId,
  });
  const vendors = (vendorsData?.vendors ?? []).filter((v) => v.is_active);

  const { data: taxRatesData } = useQuery({
    queryKey: ['bookkeeping-tax-rates', activeOrgId],
    queryFn: () => api<{ tax_rates: TaxRate[] }>('/bookkeeping/sales/tax-rates?limit=100'),
    enabled: !!activeOrgId,
  });
  const taxRates = (taxRatesData?.tax_rates ?? []).filter((r) => r.is_active);

  const { data: whRatesData } = useQuery({
    queryKey: ['bookkeeping-wh-rates', activeOrgId],
    queryFn: () =>
      api<{ withholding_rates: WithholdingRate[] }>(
        '/bookkeeping/sales/withholding-rates?limit=100',
      ),
    enabled: !!activeOrgId,
  });
  const whRates = (whRatesData?.withholding_rates ?? []).filter((r) => r.is_active);

  const { data: accountsData } = useQuery({
    queryKey: ['bookkeeping-accounts', activeOrgId],
    queryFn: () => api<{ tree: AccountNode[] }>('/bookkeeping/accounts'),
    enabled: !!activeOrgId,
  });
  const allAccounts = flattenTree(accountsData?.tree ?? []);
  const expenseAccounts = allAccounts.filter((a) => a.type === 'expense' && a.is_active);
  const assetAccounts = allAccounts.filter((a) => a.type === 'asset' && a.is_active);

  // Load existing check if editing
  useQuery({
    queryKey: ['bk-check', checkId],
    queryFn: () => api<CheckDetail>(`/bookkeeping/purchases/checks/${checkId}`),
    enabled: !isNew && !!checkId && !seeded,
    onSuccess: (data: CheckDetail) => {
      if (seeded) return;
      setPayeeType(data.check.payee_type);
      setPayeeId(data.check.payee_id ?? '');
      setPayeeName(data.check.payee_name);
      setCheckNumber(data.check.check_number ?? '');
      setPaymentDate(data.check.payment_date);
      setPaymentAccountId(data.check.payment_account_id);
      setCurrency(data.check.currency);
      setMemo(data.check.memo ?? '');
      setLines(
        data.lines.map(
          (l): CheckLineForm => ({
            account_id: l.account_id,
            description: l.description ?? '',
            amount: l.amount,
            tax_rate_id: l.tax_rate_id ?? '',
          }),
        ),
      );
      setWithholdings(
        data.withholdings.map((w) => ({
          withholding_rate_id: w.withholding_rate_id,
          base_amount: w.base_amount,
        })),
      );
      setSeeded(true);
    },
  } as Parameters<typeof useQuery>[0]);

  // When payee is a vendor, mirror their display_name into payee_name
  function selectVendor(id: string) {
    setPayeeId(id);
    const v = vendors.find((x) => x.id === id);
    if (v) setPayeeName(v.display_name);
  }

  // Live totals
  let subtotal = 0n;
  let taxTotal = 0n;
  for (const l of lines) {
    try {
      if (l.amount) {
        const a = BigInt(l.amount);
        subtotal += a;
        const tr = taxRates.find((r) => r.id === l.tax_rate_id);
        if (tr) taxTotal += (a * BigInt(tr.percentage)) / TAX_DIVISOR;
      }
    } catch {
      /* ignore */
    }
  }
  const grandTotal = subtotal + taxTotal;
  let whTotal = 0n;
  for (const w of withholdings) {
    try {
      if (w.base_amount) {
        const wr = whRates.find((r) => r.id === w.withholding_rate_id);
        if (wr) whTotal += (BigInt(w.base_amount) * BigInt(wr.percentage)) / TAX_DIVISOR;
      }
    } catch {
      /* ignore */
    }
  }
  const netAmount = grandTotal - whTotal;

  const canSubmit =
    !!payeeName &&
    !!paymentAccountId &&
    !!paymentDate &&
    effectiveCurrency.length === 3 &&
    lines.length >= 1 &&
    lines.every((l) => l.account_id && l.amount && BigInt(l.amount || '0') > 0n) &&
    (payeeType !== 'vendor' || !!payeeId);

  const submit = useMutation({
    mutationFn: () => {
      const body = JSON.stringify({
        payee_type: payeeType,
        payee_id: payeeType === 'vendor' ? payeeId : null,
        payee_name: payeeName,
        check_number: checkNumber || null,
        payment_date: paymentDate,
        payment_account_id: paymentAccountId,
        currency: effectiveCurrency,
        fx_rate: sameCurrency ? FX_SCALE.toString() : (fxData?.rate ?? FX_SCALE.toString()),
        memo: memo || null,
        lines: lines.map((l) => ({
          account_id: l.account_id,
          description: l.description || null,
          amount: l.amount,
          tax_rate_id: l.tax_rate_id || null,
        })),
        withholdings: withholdings.length > 0 ? withholdings : undefined,
      });
      return api('/bookkeeping/purchases/checks', { method: 'POST', body });
    },
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['bk-checks'] });
      nav('/app/bookkeeping/purchases/checks');
    },
    onError: (e: unknown) => {
      const err = e as { body?: { error?: string }; message?: string };
      setSubmitError(err.body?.error ?? err.message ?? 'Error');
    },
  });

  const voidMut = useMutation({
    mutationFn: () => api(`/bookkeeping/purchases/checks/${checkId}/void`, { method: 'POST' }),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['bk-checks'] });
      nav('/app/bookkeeping/purchases/checks');
    },
  });

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-semibold">
          {isNew ? t('purchases.checks.new') : t('purchases.checks.editTitle')}
        </h1>
        <button
          type="button"
          onClick={() => nav('/app/bookkeeping/purchases/checks')}
          className="rounded border px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-50"
        >
          {t('journalEntries.cancel')}
        </button>
      </div>

      {/* Header */}
      <div className="grid grid-cols-2 gap-3 md:grid-cols-3">
        {/* Payee type */}
        <div>
          <label className="block text-xs text-slate-500" htmlFor="payee-type">
            {t('purchases.checks.fields.payee_type')} *
          </label>
          <select
            id="payee-type"
            value={payeeType}
            onChange={(e) => setPayeeType(e.target.value as 'vendor' | 'employee' | 'other')}
            className="mt-0.5 w-full rounded border px-2 py-1 text-sm"
          >
            <option value="vendor">{t('purchases.checks.payee_types.vendor')}</option>
            <option value="employee">{t('purchases.checks.payee_types.employee')}</option>
            <option value="other">{t('purchases.checks.payee_types.other')}</option>
          </select>
        </div>

        {/* Vendor selector (only when payee_type=vendor) */}
        {payeeType === 'vendor' && (
          <div>
            <label className="block text-xs text-slate-500" htmlFor="vendor">
              {t('purchases.checks.fields.vendor')} *
            </label>
            <select
              id="vendor"
              value={payeeId}
              onChange={(e) => selectVendor(e.target.value)}
              className="mt-0.5 w-full rounded border px-2 py-1 text-sm"
            >
              <option value="">— {t('purchases.checks.fields.vendor')}</option>
              {vendors.map((v) => (
                <option key={v.id} value={v.id}>
                  {v.display_name}
                </option>
              ))}
            </select>
          </div>
        )}

        {/* Payee name (free text — shown for employee/other; auto-filled for vendor) */}
        <div>
          <label className="block text-xs text-slate-500" htmlFor="payee-name">
            {t('purchases.checks.fields.payee_name')} *
          </label>
          <input
            id="payee-name"
            value={payeeName}
            onChange={(e) => setPayeeName(e.target.value)}
            disabled={payeeType === 'vendor' && !!payeeId}
            placeholder={t('purchases.checks.fields.payee_name')}
            className="mt-0.5 block w-full rounded border px-2 py-1 text-sm disabled:bg-slate-50"
          />
        </div>

        {/* Check number (optional) */}
        <div>
          <label className="block text-xs text-slate-500" htmlFor="check-number">
            {t('purchases.checks.fields.check_number')}
          </label>
          <input
            id="check-number"
            value={checkNumber}
            onChange={(e) => setCheckNumber(e.target.value)}
            className="mt-0.5 block w-full rounded border px-2 py-1 text-sm"
          />
        </div>

        {/* Payment date */}
        <div>
          <label className="block text-xs text-slate-500" htmlFor="payment-date">
            {t('purchases.checks.fields.payment_date')} *
          </label>
          <input
            id="payment-date"
            type="date"
            value={paymentDate}
            onChange={(e) => setPaymentDate(e.target.value)}
            className="mt-0.5 block w-full rounded border px-2 py-1 text-sm"
          />
        </div>

        {/* Payment account */}
        <div>
          <label className="block text-xs text-slate-500" htmlFor="payment-account">
            {t('purchases.checks.fields.payment_account')} *
          </label>
          <select
            id="payment-account"
            value={paymentAccountId}
            onChange={(e) => setPaymentAccountId(e.target.value)}
            className="mt-0.5 w-full rounded border px-2 py-1 text-sm"
          >
            <option value="">— {t('purchases.checks.fields.payment_account')}</option>
            {assetAccounts.map((a) => (
              <option key={a.id} value={a.id}>
                {a.code} {a.name}
              </option>
            ))}
          </select>
        </div>

        {/* Currency */}
        <div>
          <label className="block text-xs text-slate-500" htmlFor="currency">
            {t('purchases.checks.fields.currency')}
          </label>
          <input
            id="currency"
            value={effectiveCurrency}
            maxLength={3}
            onChange={(e) => setCurrency(e.target.value.toUpperCase())}
            className="mt-0.5 block w-full rounded border px-2 py-1 text-sm uppercase"
          />
        </div>

        {/* Memo */}
        <div className="col-span-2 md:col-span-3">
          <label className="block text-xs text-slate-500" htmlFor="memo">
            {t('purchases.checks.fields.memo')}
          </label>
          <input
            id="memo"
            value={memo}
            onChange={(e) => setMemo(e.target.value)}
            className="mt-0.5 block w-full rounded border px-2 py-1 text-sm"
          />
        </div>
      </div>

      {/* Lines */}
      <div className="rounded border bg-white">
        <div className="border-b bg-slate-50 px-3 py-2 text-sm font-semibold">
          {t('purchases.checks.lines.title')}
        </div>
        <table className="w-full text-sm">
          <thead className="border-b text-left text-xs uppercase text-slate-500">
            <tr>
              <th className="px-2 py-1.5">{t('purchases.checks.lines.account')}</th>
              <th className="px-2 py-1.5">{t('purchases.checks.lines.description')}</th>
              <th className="px-2 py-1.5 text-right">{t('purchases.checks.lines.amount')}</th>
              <th className="px-2 py-1.5">{t('purchases.checks.lines.tax_rate')}</th>
              <th className="px-2 py-1.5" />
            </tr>
          </thead>
          <tbody>
            {lines.map((l, idx) => (
              // biome-ignore lint/suspicious/noArrayIndexKey: check lines are ordered form rows with no stable ID
              <tr key={idx} className="border-b last:border-0">
                <td className="px-2 py-1">
                  <select
                    value={l.account_id}
                    onChange={(e) => {
                      const next = [...lines];
                      next[idx] = { ...l, account_id: e.target.value };
                      setLines(next);
                    }}
                    className="w-full rounded border px-1.5 py-0.5 text-xs"
                  >
                    <option value="">—</option>
                    {expenseAccounts.map((a) => (
                      <option key={a.id} value={a.id}>
                        {a.code} {a.name}
                      </option>
                    ))}
                  </select>
                </td>
                <td className="px-2 py-1">
                  <input
                    value={l.description}
                    onChange={(e) => {
                      const next = [...lines];
                      next[idx] = { ...l, description: e.target.value };
                      setLines(next);
                    }}
                    className="w-full rounded border px-1.5 py-0.5 text-xs"
                  />
                </td>
                <td className="px-2 py-1">
                  <input
                    value={l.amount}
                    onChange={(e) => {
                      const next = [...lines];
                      next[idx] = { ...l, amount: e.target.value };
                      setLines(next);
                    }}
                    className="w-full rounded border px-1.5 py-0.5 text-right font-mono text-xs"
                  />
                </td>
                <td className="px-2 py-1">
                  <select
                    value={l.tax_rate_id}
                    onChange={(e) => {
                      const next = [...lines];
                      next[idx] = { ...l, tax_rate_id: e.target.value };
                      setLines(next);
                    }}
                    className="w-full rounded border px-1.5 py-0.5 text-xs"
                  >
                    <option value="">—</option>
                    {taxRates.map((r) => (
                      <option key={r.id} value={r.id}>
                        {r.code}
                      </option>
                    ))}
                  </select>
                </td>
                <td className="px-2 py-1 text-right">
                  <button
                    type="button"
                    onClick={() => setLines(lines.filter((_, i) => i !== idx))}
                    className="text-xs text-red-600 hover:underline"
                  >
                    ✕
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <div className="border-t bg-slate-50 px-3 py-2">
          <button
            type="button"
            onClick={() => setLines([...lines, defaultLine()])}
            className="text-xs text-blue-600 hover:underline"
          >
            + {t('purchases.checks.lines.add')}
          </button>
        </div>
      </div>

      {/* Withholdings */}
      <div className="rounded border bg-white">
        <div className="border-b bg-slate-50 px-3 py-2 text-sm font-semibold">
          {t('purchases.checks.withholdings.title')}
        </div>
        <table className="w-full text-sm">
          <thead className="border-b text-left text-xs uppercase text-slate-500">
            <tr>
              <th className="px-2 py-1.5">{t('purchases.checks.withholdings.rate')}</th>
              <th className="px-2 py-1.5 text-right">{t('purchases.checks.withholdings.base')}</th>
              <th className="px-2 py-1.5" />
            </tr>
          </thead>
          <tbody>
            {withholdings.map((w, idx) => (
              // biome-ignore lint/suspicious/noArrayIndexKey: withholding rows are ordered form rows with no stable ID
              <tr key={idx} className="border-b last:border-0">
                <td className="px-2 py-1">
                  <select
                    value={w.withholding_rate_id}
                    onChange={(e) => {
                      const next = [...withholdings];
                      next[idx] = { ...w, withholding_rate_id: e.target.value };
                      setWithholdings(next);
                    }}
                    className="w-full rounded border px-1.5 py-0.5 text-xs"
                  >
                    <option value="">—</option>
                    {whRates.map((r) => (
                      <option key={r.id} value={r.id}>
                        {r.code} ({(Number(r.percentage) / 1_000_000).toFixed(2)}%)
                      </option>
                    ))}
                  </select>
                </td>
                <td className="px-2 py-1">
                  <input
                    value={w.base_amount}
                    onChange={(e) => {
                      const next = [...withholdings];
                      next[idx] = { ...w, base_amount: e.target.value };
                      setWithholdings(next);
                    }}
                    className="w-full rounded border px-1.5 py-0.5 text-right font-mono text-xs"
                  />
                </td>
                <td className="px-2 py-1 text-right">
                  <button
                    type="button"
                    onClick={() => setWithholdings(withholdings.filter((_, i) => i !== idx))}
                    className="text-xs text-red-600 hover:underline"
                  >
                    ✕
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <div className="border-t bg-slate-50 px-3 py-2">
          <button
            type="button"
            onClick={() =>
              setWithholdings([...withholdings, { withholding_rate_id: '', base_amount: '' }])
            }
            className="text-xs text-blue-600 hover:underline"
          >
            + {t('purchases.checks.withholdings.add')}
          </button>
        </div>
      </div>

      {/* Totals + actions */}
      <div className="flex items-end justify-between rounded border bg-white p-3">
        <div className="space-y-0.5 text-sm">
          <div>
            {t('purchases.checks.totals.subtotal')}:{' '}
            <span className="font-mono">{subtotal.toString()}</span>
          </div>
          <div>
            {t('purchases.checks.totals.tax')}:{' '}
            <span className="font-mono">{taxTotal.toString()}</span>
          </div>
          <div>
            {t('purchases.checks.totals.grand')}:{' '}
            <span className="font-mono font-semibold">{grandTotal.toString()}</span>
          </div>
          {whTotal > 0n && (
            <div>
              {t('purchases.checks.totals.withholding')}:{' '}
              <span className="font-mono">-{whTotal.toString()}</span>
            </div>
          )}
          <div className="border-t pt-1 text-base">
            {t('purchases.checks.totals.net')}:{' '}
            <span className="font-mono font-bold">{netAmount.toString()}</span>
          </div>
        </div>
        <div className="flex gap-2">
          {!isNew && (
            <button
              type="button"
              onClick={() => voidMut.mutate()}
              className="rounded border border-red-300 px-3 py-1.5 text-sm text-red-600 hover:bg-red-50"
            >
              {t('purchases.checks.actions.void')}
            </button>
          )}
          <button
            type="button"
            disabled={!canSubmit || submit.isPending || !isNew}
            onClick={() => submit.mutate()}
            className="rounded bg-blue-600 px-3 py-1.5 text-sm text-white hover:bg-blue-700 disabled:bg-slate-300"
          >
            {t('purchases.checks.actions.submit')}
          </button>
        </div>
      </div>

      {submitError && (
        <p className="rounded border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-700">
          {submitError}
        </p>
      )}
    </div>
  );
}
